import {
  Button,
  Input,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@workspace/ui/components";
import { useState } from "react";
import { api } from "~/utils/api";
import { ClipboardButton } from "../ClipboardButton";
import LoadingSpinner from "../LoadingSpinner";
import { keepPreviousData } from "@tanstack/react-query";

export const DevPanelUserLookup = () => {
  const pageSize = 10;
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const { data: queryData, isFetching } = api.user.searchUsers.useQuery(
    { query, page, pageSize },
    { placeholderData: keepPreviousData, enabled: query !== "" }
  );

  const data = query === "" ? undefined : queryData;

  const handleSearch = () => {
    setPage(1);
    setQuery(search.trim());
  };

  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          placeholder="Search name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
        />
        <Button size="sm" onClick={handleSearch} disabled={isFetching}>
          Search
        </Button>
      </div>
      {isFetching && <LoadingSpinner size={16} color="black" />}
      {data && data.users.length > 0 && (
        <div className="space-y-2">
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="truncate">{user.name}</TableCell>
                    <TableCell>
                      <div className="flex max-w-[200px] items-center justify-between gap-1">
                        <span className="truncate">{user.email}</span>
                        <ClipboardButton text={user.email} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex max-w-[180px] items-center justify-between gap-1">
                        <span className="truncate">{user.id}</span>
                        <ClipboardButton text={user.id} />
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent className="flex flex-wrap gap-1">
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    className={
                      page === 1 ? "pointer-events-none opacity-50" : ""
                    }
                  />
                </PaginationItem>
                {(() => {
                  const set = new Set<number>();
                  for (let i = 1; i <= Math.min(3, totalPages); i++) set.add(i);
                  if (page > 3 && page < totalPages) set.add(page);
                  if (totalPages > 3) set.add(totalPages);
                  return Array.from(set)
                    .sort((a, b) => a - b)
                    .map((num) => (
                      <PaginationItem key={num}>
                        <PaginationLink
                          href="#"
                          size="sm"
                          isActive={page === num}
                          onClick={(e) => {
                            e.preventDefault();
                            setPage(num);
                          }}
                        >
                          {num}
                        </PaginationLink>
                      </PaginationItem>
                    ));
                })()}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) => Math.min(totalPages, p + 1));
                    }}
                    className={
                      page === totalPages
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}
      {data && data.users.length === 0 && <div>No users found</div>}
    </div>
  );
};
